import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { typeOfAuditorAssistanceOptions } from "@/utils/filter-utils";
import { comparisonOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  operator: comparisonOperators.enum,
  values: z.array(z.coerce.string<string>()).min(1, {
    message: "Du skal vælge mindst en revisionsbistandstype",
  }),
});

export function TypeOfAuditorAssistanceSegment({
  queryFilters,
  update,
  formId,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const typeOfAuditorAssitanceFilter = queryFilters.value;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: typeOfAuditorAssitanceFilter
      ? schema.parse(typeOfAuditorAssitanceFilter)
      : {
          operator: "Equal",
          values: [],
        },
  });

  useReportDirty(isDirty, setIsDirty);

  useEffect(() => {
    console.debug("Form errors", errors);
  }, [errors]);

  const { values, operator } = watch();

  const submit = handleSubmit((data) => {
    console.debug("Submitting", data);
    update({
      value: { ...data, values: data.values.map(String) },
    });
  });

  return (
    <div className="flex flex-col gap-y-9">
      <SegmentHeader
        title="Type af revisionsbistand"
        description="Angiver niveauet for, hvor grundigt regnskabet er gennemgået af en revisor. En lavere erklæring betyder begrænset eller ingen gennemgang, mens en højere erklæring (som udvidet gennemgang eller fuld revision) giver større sikkerhed for regnskabets pålidelighed."
      />
      <Form
        onSubmit={submit}
        className="flex size-full h-full flex-col items-center justify-between gap-y-12"
        id={formId}
      >
        <div className="flex w-full flex-col gap-y-6">
          <div className="mx-auto flex w-full max-w-140 gap-x-4">
            <Controller
              control={control}
              name="operator"
              render={({ field, fieldState: { error } }) => (
                <Select
                  placeholder="Operator"
                  aria-label="Operator"
                  selectedKey={field.value}
                  onSelectionChange={field.onChange}
                  items={Object.entries(comparisonOperators.values).map(
                    ([key, value]) => ({
                      id: key,
                      label: value,
                    }),
                  )}
                  errorMessage={error?.message}
                >
                  {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
              )}
            />
            <Controller
              control={control}
              name="values"
              render={({ field, fieldState: { error } }) => (
                <TagField
                  className="w-full"
                  placeholder="Type af revisionsbistand"
                  aria-label="Type af revisionsbistand"
                  selectedOptions={
                    field.value.map((value) => ({
                      label: typeOfAuditorAssistanceOptions.find(
                        (m) => m.value === parseInt(value),
                      )!.title,
                      value,
                    })) || []
                  }
                  setSelectedOptions={(value) => {
                    if (Array.isArray(value))
                      return field.onChange(value.map((item) => item.value));
                  }}
                  options={typeOfAuditorAssistanceOptions.map(
                    (typeOfAuditorAssistance) => ({
                      label: typeOfAuditorAssistance.title,
                      value: typeOfAuditorAssistance.value,
                    }),
                  )}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <ul className="flex flex-col gap-y-2">
            {values.map((value, index) => (
              <ListItem
                key={value}
                label={`${operator === "Equal" ? "Lig med" : "Ikke lig med"} ${
                  typeOfAuditorAssistanceOptions.find(
                    (m) => m.value === parseInt(value),
                  )?.title
                }`}
                onRemove={() => {
                  const newValues = [...values];
                  newValues.splice(index, 1);
                  setValue("values", newValues);
                }}
              />
            ))}
          </ul>
        </div>
      </Form>
    </div>
  );
}
